import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, RefreshCw, LogOut } from "lucide-react";
import { adminLogin, getAdminAppointments, setAppointmentStatus, getPublicSettings, saveAdminSettings } from "@/lib/api";

const STATUS_LABEL = { pending: "Offen", accepted: "Angenommen", rejected: "Abgelehnt" };

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "border-[#B08D57] text-[#B08D57]",
    accepted: "border-[#6B8F71] text-[#6B8F71] bg-[#6B8F71]/10",
    rejected: "border-[#24211D]/30 text-[#24211D]/50",
  };
  return (
    <span className={`inline-block border px-3 py-1 text-[10px] uppercase tracking-[0.14em] font-sans ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function Anfragen() {
  const [token, setToken] = useState(() => sessionStorage.getItem("nes-admin-token") || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewUrl, setReviewUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    getPublicSettings()
      .then(({ data }) => setReviewUrl(data.google_review_url || ""))
      .catch(() => {});
  }, []);

  const saveReviewUrl = async (e) => {
    e.preventDefault();
    setSavingUrl(true);
    try {
      await saveAdminSettings(token, { google_review_url: reviewUrl });
      toast.success(reviewUrl ? "Bewertungs-Link gespeichert – er ist jetzt auf der Startseite sichtbar." : "Bewertungs-Link entfernt.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setSavingUrl(false);
    }
  };

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await getAdminAppointments(t || token);
      setAppointments(data.appointments || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("nes-admin-token");
        setToken("");
        toast.error("Sitzung abgelaufen – bitte neu anmelden.");
      } else {
        toast.error("Anfragen konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await adminLogin(password);
      sessionStorage.setItem("nes-admin-token", data.token);
      setToken(data.token);
      toast.success("Angemeldet – willkommen im Studio-Bereich.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anmeldung fehlgeschlagen.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("nes-admin-token");
    setToken("");
    setAppointments([]);
  };

  const setStatus = async (id, status) => {
    try {
      await setAppointmentStatus(token, id, status);
      setAppointments((list) => list.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(status === "accepted" ? "Termin angenommen." : "Termin abgelehnt.");
    } catch (err) {
      toast.error("Status konnte nicht gespeichert werden.");
    }
  };

  const isConflict = (a) =>
    a.status !== "rejected" &&
    appointments.some((b) => b.id !== a.id && b.date === a.date && b.time === a.time && b.status === "accepted");

  if (!token) {
    return (
      <div className="py-20" data-testid="page-studio-login">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Studio-Bereich</span>
            <h1 className="text-3xl md:text-4xl">Anmelden</h1>
            <p className="text-xs text-[#24211D]/70 font-sans mt-3">Nur für das Haarstudio Nes Team – hier verwaltest du die Terminanfragen.</p>
          </div>
          <form onSubmit={login} className="bg-[#FFFFFF] border border-[#24211D]/15 p-8 space-y-6" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-password" className="nes-label">Passwort</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                className="nes-input"
                placeholder="Studio-Passwort"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit"
              className="w-full bg-[#B08D57] hover:bg-[#967442] disabled:opacity-60 text-[#FBF8F3] px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-300"
            >
              {loggingIn ? "Prüfe ..." : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16" data-testid="page-studio">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Studio-Bereich</span>
            <h1 className="text-3xl md:text-5xl">Terminanfragen</h1>
            <p className="text-xs text-[#24211D]/70 font-sans mt-2">
              Nimm Anfragen an oder lehne sie ab. Angenommene Termine blockieren den Zeitraum für neue Online-Anfragen.
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load()} data-testid="admin-refresh-btn" className="flex items-center gap-2 border border-[#24211D]/30 text-[#24211D] px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] font-sans hover:border-[#B08D57] hover:text-[#B08D57] transition-colors duration-200">
              <RefreshCw className="w-4 h-4" /> Aktualisieren
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 border border-[#24211D]/30 text-[#24211D]/70 px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] font-sans hover:border-[#24211D] transition-colors duration-200">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
          </div>
        </div>

        {/* Google-Bewertungslink verwalten */}
        <div className="border border-[#24211D]/15 bg-[#FFFFFF] p-6 sm:p-8 mb-10" data-testid="review-link-card">
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Google-Bewertungen</span>
          <h2 className="text-2xl mb-3">Dein Bewertungs-Link</h2>
          <p className="text-xs text-[#24211D]/70 font-sans leading-relaxed mb-4">
            So bekommst du den Link: 1) Erstelle ein kostenloses Google-Konto (oder nutze deins) und melde dich bei <strong>google.com</strong> an.
            2) Suche bei Google oder in Google Maps nach <strong>„Haarstudio Nes Glessen"</strong> und tippe im Profil auf <strong>„Sind Sie der Inhaber?"</strong> bzw. <strong>„Dieses Unternehmen übernehmen"</strong>.
            3) Nach der Freischaltung suchst du eingeloggt erneut nach dem Salon – dann erscheint dein Bearbeitungs-Bereich direkt in der Suche. Dort unter <strong>„Bewertungen"</strong> → <strong>„Mehr Bewertungen erhalten"</strong> den Link kopieren.
            4) Den Link hier einfügen und speichern – danach erscheint auf der Startseite automatisch der „Bewerte uns"-Button.
          </p>
          <form onSubmit={saveReviewUrl} className="flex flex-col sm:flex-row gap-4">
            <input
              type="url"
              data-testid="review-url-input"
              className="nes-input flex-1"
              placeholder="https://g.page/r/... "
              value={reviewUrl}
              onChange={(e) => setReviewUrl(e.target.value)}
            />
            <button type="submit" disabled={savingUrl} data-testid="review-url-save-btn" className="bg-[#B08D57] hover:bg-[#967442] disabled:opacity-60 text-[#FBF8F3] px-8 py-3 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors duration-200">
              {savingUrl ? "Speichert ..." : "Speichern"}
            </button>
          </form>
          {reviewUrl && (
            <p className="text-[11px] font-sans text-[#6B8F71] mt-3" data-testid="review-url-active">
              Aktiv – dieser Link wird auf der Startseite gezeigt: {reviewUrl}
            </p>
          )}
        </div>

        {loading && appointments.length === 0 ? (
          <p className="text-sm text-[#24211D]/60 font-sans">Lade Anfragen ...</p>
        ) : appointments.length === 0 ? (
          <div className="border border-[#24211D]/15 bg-[#FFFFFF] p-10 text-center" data-testid="admin-empty-state">
            <h2 className="text-2xl mb-2">Noch keine Anfragen</h2>
            <p className="text-xs text-[#24211D]/70 font-sans">Sobald jemand online einen Termin anfragt, erscheint er hier.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="admin-appointments-list">
            {appointments.map((a) => (
              <div key={a.id} className="border border-[#24211D]/15 bg-[#FFFFFF] p-6 sm:p-8" data-testid={`appointment-card-${a.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-2xl">{a.name}</h2>
                    <a href={`tel:${a.phone.replace(/[^0-9+]/g, "")}`} data-testid={`appointment-phone-${a.id}`} className="inline-flex items-center gap-2 text-sm text-[#B08D57] hover:text-[#24211D] font-sans transition-colors duration-200">
                      <Phone className="w-4 h-4" /> {a.phone}
                    </a>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm mb-4">
                  <div>
                    <span className="nes-label">Leistung</span>
                    <span className="block">{a.service}</span>
                  </div>
                  <div>
                    <span className="nes-label">Wunschtermin</span>
                    <span className="block font-sans font-bold text-xs pt-1">{formatDate(a.date)} · {a.time} Uhr</span>
                  </div>
                  <div>
                    <span className="nes-label">Eingegangen</span>
                    <span className="block text-xs text-[#24211D]/70 font-sans pt-1">{a.created_at ? new Date(a.created_at).toLocaleString("de-DE") : "–"}</span>
                  </div>
                </div>
                {a.message && (
                  <p className="text-xs text-[#24211D]/75 font-sans border-t border-[#24211D]/10 pt-3 mb-4">„{a.message}"</p>
                )}
                {isConflict(a) && (
                  <p className="text-[11px] font-sans text-[#B08D57] border border-[#B08D57]/40 bg-[#B08D57]/5 px-3 py-2 mb-4" data-testid={`conflict-warning-${a.id}`}>
                    Achtung: Für diesen Zeitraum ist bereits ein angenommener Termin eingetragen.
                  </p>
                )}
                {a.status === "pending" && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button onClick={() => setStatus(a.id, "accepted")} data-testid={`accept-btn-${a.id}`} className="bg-[#6B8F71] hover:bg-[#5A7A5F] text-[#FBF8F3] px-6 py-3 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors duration-200">
                      Annehmen
                    </button>
                    <button onClick={() => setStatus(a.id, "rejected")} data-testid={`reject-btn-${a.id}`} className="border border-[#24211D]/30 text-[#24211D]/70 hover:border-[#24211D] px-6 py-3 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors duration-200">
                      Ablehnen
                    </button>
                  </div>
                )}
                {a.status !== "pending" && (
                  <button onClick={() => setStatus(a.id, "pending")} data-testid={`reopen-btn-${a.id}`} className="text-[11px] uppercase tracking-[0.14em] font-sans text-[#24211D]/50 hover:text-[#B08D57] underline underline-offset-4 transition-colors duration-200">
                    Wieder auf „Offen" setzen
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
